import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

import { errorMessageFromResponse } from '../../helpers/errors';
import * as API from '../api';
import * as IssuesV2Store from '../issuesV2';
import { toAsyncActionTypes } from '../utils';
import type { AnySort, Category, FilterByCategory, Grouping } from '../../../shared/issuesV2';
import type { SaveFilterRequest, SaveNewFilterRequest } from '../../../shared/savedFilters/request';
import type { GetAllFiltersResponse } from '../../../shared/savedFilters/response';
import type { AddToast } from '../../components/Toaster/types';
import type { FailedFetchResponse } from '../../helpers/errors';
import type { ReduxAction } from '../../types';
import type { ReduxStore } from '../store';

import * as savedFiltersSelectors from './selectors';
import { setCurrentFilter } from './sharedActions';

//  Same endpoint for all requests (different HTTP method for each).
const ENDPOINT = '/api/issue-filters';

//  All of the API requests for savedFilters.
const savedFiltersAPI = {
  GET_FILTERS: (category: Category) =>
    API.get<GetAllFiltersResponse>(ENDPOINT, true, { category }, { encodeQuery: true }),
  SAVE_AS: <C extends Category>(
    category: C,
    name: string,
    filter: FilterByCategory<C>,
    sort?: AnySort,
    group?: Grouping,
  ) => API.post<SaveNewFilterRequest<C>, void>(ENDPOINT, { category, name, filter, sort, group }),
  SAVE: <C extends Category>(
    id: number,
    name?: string,
    filter?: FilterByCategory<C>,
    sort?: AnySort,
    group?: Grouping,
  ) => API.put<SaveFilterRequest<C>, void>(`${ENDPOINT}/${id}`, { name, filter, sort, group }),
  DELETE: (id: number) => API.remove(`${ENDPOINT}/${id}`),
};

export const actionTypes = {
  sync: {
    //  Note: this action creator lives in `./sharedActions`
    SET_CURRENT_FILTER: 'SAVED_FILTERS_SET_CURRENT_FILTER',
    SET_HAS_CHANGED: 'SAVED_FILTERS_SET_HAS_CHANGED',
  },
  async: {
    GET_FILTERS: 'SAVED_FILTERS_GET_FILTERS',
    SAVE_FILTER: 'SAVED_FILTERS_SAVE_FILTER',
    SAVE_FILTER_AS: 'SAVED_FILTERS_SAVE_FILTER_AS',
    RENAME_FILTER: 'SAVED_FILTERS_RENAME_FILTER',
    DELETE_FILTER: 'SAVED_FILTERS_DELETE_FILTER',
  },
};

//  All of the async action types, but async-ified to have a PENDING, FULFILLED, ETC.
export const asyncActionTypes = Object.freeze(toAsyncActionTypes(actionTypes.async));

/**
 * Action for returning the list of saved filters.
 */
const getFiltersAction = (category: Category): ReduxAction<GetAllFiltersResponse> => ({
  type: actionTypes.async.GET_FILTERS,
  //  Include the category so we know where in `state.filters` to put the response data.
  meta: { category },
  payload: async () => savedFiltersAPI.GET_FILTERS(category),
});

export const getFilters =
  (category: Category, addToast?: AddToast): ThunkAction<void, ReduxStore, never, AnyAction> =>
    async (dispatch, getState) => {
      try {
        await dispatch(getFiltersAction(category));
        //  If there is a currentFilter, it means that it was set via the `savedFilter` query param,
        //  however because the filter data has only been loaded now, we ensure that whatever the
        //  `currentFilter` is will actually be set.
        const currentFilter = savedFiltersSelectors.getCurrentFilter(getState());
        if (currentFilter) {
          dispatch(setCurrentFilter(currentFilter));
        }
      } catch (error) {
        const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
        if (addToast) {
          addToast({ type: 'error', content: errorMessage });
        }
      }
    };

/**
 * Action for saving the current filter state.
 */
const saveFilterAction = <C extends Category>(
  category: C,
  id: number,
  name: string,
  filter?: FilterByCategory<C>,
  sort?: AnySort,
  group?: Grouping,
): ReduxAction => ({
    type: actionTypes.async.SAVE_FILTER,
    //  Include the category so we know where in `state.filters` to update the response data.
    meta: { category },
    payload: async () => savedFiltersAPI.SAVE(id, name, filter, sort, group),
  });

//  Thunk for the above action, which pulls data from the state and passes it to the action.
export const saveFilter = (): ThunkAction<void, ReduxStore, never, AnyAction> => (dispatch, getState) => {
  const state = getState();
  const category = IssuesV2Store.selectors.getIssueCategory(state);
  const currentFilter = savedFiltersSelectors.getCurrentFilterByCategory(state, category);
  const filter = savedFiltersSelectors.getCurrentFilterState(state);
  const group = IssuesV2Store.selectors.getGrouping(state);
  const sort = IssuesV2Store.selectors.getSort(state);

  //  This would never happen, because the option is disabled if no filter is selected.
  if (currentFilter) {
    return dispatch(saveFilterAction(category, currentFilter.id, currentFilter.name, filter, sort, group));
  }
  return null;
};

/**
 * Action for saving the current filter state with a new name
 */
const saveFilterAsAction = <C extends Category>(
  category: C,
  name: string,
  filter: FilterByCategory<C>,
  sort: AnySort,
  group: Grouping,
) => ({
    type: actionTypes.async.SAVE_FILTER_AS,
    //  Include the category so we know where in `state.filters` to update the response data.
    meta: { category },
    payload: async () => savedFiltersAPI.SAVE_AS(category, name, filter, sort, group),
  });

//  Thunk for the above action, which pulls data from the state and passes it to the action.
export const saveFilterAs = (name: string): ThunkAction<void, ReduxStore, never, AnyAction> => {
  return (dispatch, getState) => {
    const state = getState();
    const category = IssuesV2Store.selectors.getIssueCategory(state);
    const filter = savedFiltersSelectors.getCurrentFilterState(state);
    const group = IssuesV2Store.selectors.getGrouping(state);
    const sort = IssuesV2Store.selectors.getSort(state);

    return dispatch(saveFilterAsAction(category, name, filter, sort, group));
  };
};

/**
 * Action for renaming the current filter.
 */
const renameFilterAction = (category: Category, id: number, name: string) => ({
  type: actionTypes.async.RENAME_FILTER,
  //  Include the category so we know where in `state.filters` to update the response data.
  meta: { category },
  payload: async () => savedFiltersAPI.SAVE(id, name),
});

//  Thunk for the above action, which pulls data from the state and passes it to the action.
export const renameFilter =
  (name: string): ThunkAction<void, ReduxStore, never, AnyAction> =>
    (dispatch, getState) => {
      const state = getState();
      const category = IssuesV2Store.selectors.getIssueCategory(state);
      const currentFilter = savedFiltersSelectors.getCurrentFilterByCategory(state, category);

      //  This would never happen, because the option is disabled if no filter is selected.
      if (currentFilter) {
        return dispatch(renameFilterAction(category, currentFilter.id, name));
      }
      return null;
    };

/**
 * Action for deleting the current filter.
 */
const deleteFilterAction = (category: Category, id: number) => ({
  type: actionTypes.async.DELETE_FILTER,
  //  Include the category so we know where in `state.filters` to update the response data.
  meta: { category },
  payload: async () => savedFiltersAPI.DELETE(id),
});

//  Thunk for the above action, which pulls data from the state and passes it to the action.
export const deleteFilter = (): ThunkAction<void, ReduxStore, never, AnyAction> => async (dispatch, getState) => {
  const state = getState();
  const category = IssuesV2Store.selectors.getIssueCategory(state);
  const currentFilter = savedFiltersSelectors.getCurrentFilterByCategory(state, category);

  //  This would never happen, because the option is disabled if no filter is selected.
  if (currentFilter) {
    dispatch(deleteFilterAction(category, currentFilter.id));
    //  Clear the current filter and re-fetch the issues + status summary
    dispatch(setCurrentFilter(null));
    dispatch(IssuesV2Store.actions.fetchIssues());
    return dispatch(IssuesV2Store.actions.fetchStatusSummary()); // Returned to keep the promise chainable/awaitable, if needed.
  }
  return null;
};

//  Also ensure that setCurrentFilter can be accessed like SavedFiltersActions.actions.setCurrentFilter
export { setCurrentFilter };
